import getConfig from 'next/config';
import { useState } from 'react';
import { Tooltip } from '../../../../../../../Atoms/Tooltip';
import { CopyIcon } from '../../../../../../../Icons/copy/Copy';
import { addQueryParamToURL } from '../../../../../../../Utils/CommonUtils';
import { ButtonWithAnchor } from '../../../../../../../income-tax/page-sections/hero-section/sticky-button';
import { getPercentageDiff } from '../../../../../../../partner-pages/price_cards/utils';
import {
  getCheckoutUrl,
  getPlanPageUrl,
} from '../../../../../../../partner-pages/utils/utils';
import { PLAN_DATA } from '../../../types';
const { publicRuntimeConfig } = getConfig();

interface PlanCardProps {
  title: string;
  desc: string;
  icon: string;
  buttonText: string;
  bgImage: string;
  bgColor: string;
  className: string;
  isAssistedFiling: boolean;
  planData: PLAN_DATA | undefined;
  showTopStrip?: boolean;
}

export const PlanCard = ({
  title,
  desc,
  icon,
  buttonText,
  bgImage,
  bgColor,
  className,
  isAssistedFiling,
  planData,
  showTopStrip,
}: PlanCardProps) => {
  const [isCopied, setIsCopied] = useState(false);

  if (!planData) return null;

  const { marketplacePlan } = planData;
  const coupon =
    marketplacePlan.coupons && marketplacePlan.coupons.length > 0
      ? marketplacePlan.coupons[0]
      : { code: 'CTEXPERTS' };

  const discountedPrice = Number(
    marketplacePlan.pricing.discountedPrice
  ).toFixed(0);
  const planAmount = Number(marketplacePlan.pricing.planAmount).toFixed(0);
  const link = `${publicRuntimeConfig.CLEARTAX_DOMAIN}/filing/?showplan=1&utm_ref=plan_selector`;
  const heroBtnLink = addQueryParamToURL(
    isAssistedFiling ? getCheckoutUrl(marketplacePlan.externalId) : link,
    'coupon',
    coupon?.code
  );
  const knowMoreLink = !isAssistedFiling
    ? link
    : getPlanPageUrl(marketplacePlan.externalId);
  const priceDiff = getPercentageDiff(+planAmount, +discountedPrice);
  const showOfferPrice = priceDiff <= 0 ? 'hidden' : '';

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      console.log('Text copied to clipboard');
      setIsCopied(true);
    } catch (error) {
      console.error('Failed to copy text: ', error);
    }
  };

  return (
    <div className="relative">
      {showTopStrip ? (
        <div className="py-1 px-2 rounded-tl-md rounded-tr-md bg-[#E6B42D] w-fit text-white mx-auto text-s-14 h-7">
          Over 20K people purchased this plan
        </div>
      ) : (
        <div className="h-7"></div>
      )}
      <div className="rounded-xl w-full" style={{ backgroundColor: bgColor }}>
        <div
          className="p-6 rounded-xl"
          style={{ background: `url(${bgImage})` }}
        >
          <div className="flex justify-between">
            <div className="text-[#07244B] text-s-32 font-bold mb-5">
              {title}
            </div>
            <img src={icon} width={48} height={48} alt="" />
          </div>
          <div className="w-11/12">
            <div className="text-[#07244B] text-s-20 h-12">{desc}</div>
            <div className="pt-9">
              <span className="text-[#07244B] text-s-32 font-bold">
                ₹{discountedPrice}
              </span>
              <span
                className={`line-through text-font-200 pl-3 pr-2 ${showOfferPrice}`}
              >
                ₹{planAmount}
              </span>
              <span className={`text-[#16CC65] ${showOfferPrice}`}>
                ({getPercentageDiff(+planAmount, +discountedPrice)}% OFF)
              </span>
            </div>
            <div
              className={`text-font-200 text-s-14 ${
                priceDiff <= 0 ? 'invisible' : ''
              }`}
            >
              Offer price (excl. of taxes)
            </div>

            {coupon ? (
              <div>
                <div
                  className="flex items-center gap-4 mt-7 mb-10"
                  onClick={() => copyToClipboard(coupon.code)}
                >
                  <Tooltip
                    content={
                      <div className="bg-[#041832] px-2 mt-1 rounded-sm flex justify-center text-white text-s-12 text-center">
                        {isCopied
                          ? `Copied ${coupon.code}`
                          : 'Click to copy and apply it during checkout'}
                      </div>
                    }
                    hideCloseIcon={true}
                    showOnHover={true}
                    icon=""
                    direction="bottom"
                    contentPosition={'absolute'}
                  >
                    <div className="flex items-center gap-2 py-2 px-3 border border-[#E3DAC3] rounded-md font-bold">
                      {coupon?.code} <CopyIcon />
                    </div>
                  </Tooltip>

                  <div>Use Code for better deal</div>
                </div>
              </div>
            ) : null}
          </div>

          <ButtonWithAnchor
            trackLabel="plan_selector_button"
            buttonText={buttonText}
            buttonLink={heroBtnLink}
            className={`w-full border border-brand-1000 text-brand-1000 text-s-14 ${className}`}
          />

          {isAssistedFiling ? (
            <div className=" text-center">
              <a href={knowMoreLink} className="text-s-14 mt-2 text-blue-350">
                Know more
              </a>
            </div>
          ) : (
            <div className="text-s-14 mt-2 text-center">
              Pay only when you file.{' '}
              <a href={knowMoreLink} className="text-blue-350 text-s-14">
                Know more
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
